import React, { useState } from "react";
import { Copy, Check, Sparkle, DownloadSimple } from "@phosphor-icons/react";

export const downloadAsText = (text, filename = "content.txt") => {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadFromUrl = async (url, filename) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || url.split("/").pop() || "download";
    a.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank");
  }
};

export const PlatformBadge = ({ platform }) => {
  const classes = {
    tiktok: "badge-tiktok",
    instagram: "badge-instagram",
    linkedin: "badge-linkedin",
  };
  return (
    <span className={classes[platform] || "badge-tiktok"}>
      {platform?.charAt(0).toUpperCase() + platform?.slice(1)}
    </span>
  );
};

export const CopyButton = ({ text, label = "Copy" }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      data-testid={`copy-${label.toLowerCase().replace(/\s/g, "-")}-btn`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
        copied ? "bg-green-500 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
      }`}
    >
      {copied ? <Check size={16} /> : <Copy size={16} />}
      {copied ? "Copied!" : label}
    </button>
  );
};

export const ChipSelector = ({ options, selected, onChange, label }) => (
  <div className="space-y-2">
    {label && <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</label>}
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const value = option.value !== undefined ? option.value : option;
        const displayLabel = option.label || String(option);
        const valueStr = String(value);
        return (
          <button
            key={valueStr}
            onClick={() => onChange(value)}
            data-testid={`chip-${valueStr.toLowerCase().replace(/\s/g, "-")}`}
            className={`chip ${selected === value ? "chip-active" : "chip-default"}`}
          >
            {displayLabel}
          </button>
        );
      })}
    </div>
  </div>
);

export const LoadingSpinner = () => (
  <div className="flex items-center justify-center p-8">
    <div className="w-8 h-8 border-3 border-[#E91E8C] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export const formatAIContent = (content) => {
  if (!content) return "";
  try {
    let jsonStr = content;
    if (content.includes("```json")) {
      jsonStr = content.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
    } else if (content.includes("```")) {
      jsonStr = content.replace(/```\n?/g, "").trim();
    }
    const parsed = JSON.parse(jsonStr);
    if (Array.isArray(parsed)) {
      return parsed.map((item, i) => {
        if (item.topic && item.format && item.angle) {
          return `${i + 1}. ${item.topic}\n   Format: ${item.format}\n   Angle: ${item.angle}`;
        }
        return JSON.stringify(item, null, 2);
      }).join("\n\n");
    }
    if (parsed.hook && parsed.caption && parsed.hashtags) {
      return `HOOK:\n${parsed.hook}\n\nCAPTION:\n${parsed.caption}\n\nHASHTAGS:\n${parsed.hashtags.map(h => `#${h.replace('#', '')}`).join(" ")}`;
    }
    if (parsed.post && parsed.first_comment) {
      return `POST:\n${parsed.post}\n\nFIRST COMMENT:\n${parsed.first_comment}\n\nHASHTAGS:\n${(parsed.hashtags || []).map(h => `#${h.replace('#', '')}`).join(" ")}`;
    }
    if (parsed.script && parsed.caption) {
      let output = `SCRIPT:\n${parsed.script}`;
      if (parsed.word_count) output += `\n\n(Word count: ${parsed.word_count})`;
      output += `\n\nCAPTION:\n${parsed.caption}`;
      if (parsed.hashtags) output += `\n\nHASHTAGS:\n${parsed.hashtags.map(h => `#${h.replace('#', '')}`).join(" ")}`;
      if (parsed.youtube_description) output += `\n\nYOUTUBE DESCRIPTION:\n${parsed.youtube_description}`;
      return output;
    }
    if (parsed.frames) {
      let output = "LIFESTYLE VIDEO PLAN\n\n";
      parsed.frames.forEach((frame, i) => {
        output += `FRAME ${i + 1}:\nImage Prompt: ${frame.image_prompt}\nMotion: ${frame.motion}\n`;
        if (frame.text_overlay) output += `Text: "${frame.text_overlay.text}" (${frame.text_overlay.position})\n`;
        output += "\n";
      });
      if (parsed.sound_direction) output += `SOUND DIRECTION:\n${parsed.sound_direction.join(", ")}\n\n`;
      if (parsed.caption) output += `CAPTION:\n${parsed.caption}\n\n`;
      if (parsed.hashtags) output += `HASHTAGS:\n${parsed.hashtags.map(h => `#${h.replace('#', '')}`).join(" ")}`;
      return output;
    }
    if (parsed.scene_options || parsed.full_prompt) {
      let output = "";
      if (parsed.scene_options) {
        output += "SCENE OPTIONS:\n";
        parsed.scene_options.forEach((s, i) => output += `${i + 1}. ${s}\n`);
        output += "\n";
      }
      if (parsed.selected_scene) output += `SELECTED SCENE:\n${parsed.selected_scene}\n\n`;
      if (parsed.why_selected) output += `WHY:\n${parsed.why_selected}\n\n`;
      if (parsed.full_prompt) output += `HIGGSFIELD PROMPT:\n${parsed.full_prompt}`;
      return output;
    }
    if (parsed.monday || parsed.wednesday || parsed.friday) {
      let output = "";
      ["monday", "wednesday", "friday"].forEach(day => {
        if (parsed[day]) {
          output += `${day.toUpperCase()}:\nTopic: ${parsed[day].topic}\nFormat: ${parsed[day].format}\n\nPOST:\n${parsed[day].post}\n\nFIRST COMMENT:\n${parsed[day].first_comment}\n\nHASHTAGS: ${(parsed[day].hashtags || []).map(h => `#${h.replace('#', '')}`).join(" ")}\n\n---\n\n`;
        }
      });
      return output;
    }
    return Object.entries(parsed).map(([key, value]) => {
      if (Array.isArray(value)) return `${key.toUpperCase()}:\n${value.join(", ")}`;
      return `${key.toUpperCase()}:\n${value}`;
    }).join("\n\n");
  } catch (e) {
    return content.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
  }
};

export const AIOutput = ({ content, loading, label = "Generated Content" }) => {
  if (loading) {
    return (
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkle size={20} className="text-[#E91E8C] animate-pulse" />
          <span className="text-zinc-500">Generating content...</span>
        </div>
        <div className="space-y-3">
          <div className="h-4 bg-zinc-100 rounded animate-pulse"></div>
          <div className="h-4 bg-zinc-100 rounded animate-pulse w-3/4"></div>
          <div className="h-4 bg-zinc-100 rounded animate-pulse w-1/2"></div>
        </div>
      </div>
    );
  }
  if (!content) return null;
  const formattedContent = formatAIContent(content);
  return (
    <div className="card p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-zinc-900">{label}</h4>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadAsText(formattedContent, `${label.replace(/\s+/g, "_").toLowerCase()}.txt`)}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#E91E8C] transition-all px-2.5 py-1.5 rounded-lg hover:bg-zinc-50"
            data-testid="download-content-btn"
          >
            <DownloadSimple size={14} />
            Download
          </button>
          <CopyButton text={formattedContent} label="Copy All" />
        </div>
      </div>
      <div className="bg-zinc-50 rounded-lg p-4 whitespace-pre-wrap text-zinc-700 text-sm leading-relaxed max-h-[500px] overflow-y-auto">
        {formattedContent}
      </div>
    </div>
  );
};
